import { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Anchor, Checkbox, Divider, Modal, Stack, Text } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import moment from 'moment';
import Autoload from '../neoverv/Autoload';
import APIClient from '../neoverv/config/APIClient';
import { config } from '../neoverv/config/Setting';
import ColorButton from '../neoverv/components/Module/General/Buttons/ColorButton';
import RedCross from '../assets/images/module/general/RedCross.svg';
import RightArrowPartner from '../assets/images/module/general/RightArrowPartner.svg';
import { setAccountData } from '../redux/action/General/generalActions';
import {
  LEGAL_DOCUMENT_LINKS,
  LEGAL_DOCUMENT_VERSIONS,
  isDataPolicyAccepted,
  isDataProcessingAccepted,
  isGeneralTermsAccepted,
  needsLegalConsent,
} from '../helpers/legalConsent';

const SECTIONS = [
  {
    key: 'generalTerms',
    heading: 'Allgemeine Geschäftsbedingungen (AGB)',
    versionDate: LEGAL_DOCUMENT_VERSIONS.generalTerms,
    link: LEGAL_DOCUMENT_LINKS.generalTerms,
    checkboxLabel: 'Ich stimme den Allgemeinen Geschäftsbedingungen zu.',
    isAccepted: isGeneralTermsAccepted,
    consentName: 'GeneralTerms',
  },
  {
    key: 'dataPolicy',
    heading: 'Datenschutzerklärung für die hellohousing WebApp',
    versionDate: LEGAL_DOCUMENT_VERSIONS.dataPolicy,
    link: LEGAL_DOCUMENT_LINKS.dataPolicy,
    checkboxLabel: 'Ich habe die Datenschutzerklärung zur Kenntnis genommen.',
    isAccepted: isDataPolicyAccepted,
    consentName: 'DataPolicy',
  },
  {
    key: 'dataProcessing',
    heading: 'Vereinbarung zur Auftragsverarbeitung (AVV)',
    versionDate: LEGAL_DOCUMENT_VERSIONS.dataProcessing,
    link: LEGAL_DOCUMENT_LINKS.dataProcessing,
    checkboxLabel: 'Ich stimme der Vereinbarung zur Auftragsverarbeitung zu.',
    isAccepted: isDataProcessingAccepted,
    consentName: 'DataProcessing',
  },
];

function LegalConsentModal({ accountData, setAccountData }) {
  const opened = needsLegalConsent(accountData);
  const pendingSections = useMemo(
    () => SECTIONS.filter((section) => accountData && !section.isAccepted(accountData)),
    [accountData]
  );
  const pendingKey = pendingSections.map((section) => section.key).join(',');

  const [checked, setChecked] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Re-seed the checkboxes whenever a fresh set of documents needs (re-)consent.
  useEffect(() => {
    setChecked({});
  }, [pendingKey]);

  if (!opened) {
    return null;
  }

  const allChecked = pendingSections.every((section) => checked[section.key]);

  const handleConfirm = async () => {
    if (!allChecked || submitting) {
      showNotification({
        icon: <img src={RedCross} alt="red-cross" />,
        message: 'Bitte bestätigen Sie alle erforderlichen Dokumente, bevor Sie fortfahren.',
        id: 'legal-consent-modal-notification',
        zIndex: 1000001,
      });
      return;
    }
    setSubmitting(true);
    const payload = { consents: pendingSections.map((section) => section.consentName) };

    try {
      await APIClient({
        method: 'PATCH',
        url: config.accountConsents,
        data: payload,
        headers: { Authorization: Autoload.Utils.getAuthToken() },
      });
      // Re-fetch rather than trust the POST response, same as the ai-consent
      // flow in ChatApp.jsx — the backend owns the actual accepted-at values.
      const refreshed = await APIClient({
        method: 'GET',
        url: Autoload.Setting.account_api_baseurl + accountData.accountId,
        headers: { Authorization: Autoload.Utils.getAuthToken() },
      });
      if (refreshed.status === 200 && refreshed.data?.result) {
        setAccountData({ ...accountData, ...refreshed.data.result });
      }
    } catch (e) {
      showNotification({
        icon: <img src={RedCross} alt="red-cross" />,
        message: 'Ihre Zustimmung konnte nicht gespeichert werden. Bitte versuchen Sie es erneut.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={() => {}}
      withCloseButton={false}
      closeOnEscape={false}
      closeOnClickOutside={false}
      centered
      size={560}
      zIndex={1000000}
      styles={{ header: { paddingBottom: '0px' }}}
      title={<span style={{ fontSize: '18px', fontWeight: 600, padding: '0px 10px' }}>Wir benötigen Ihre Zustimmung</span>}
    >
      <Stack gap="md" style={{ padding: '10px' }}>
        <Text>
          Für die weitere Nutzung von hellohousing ist Ihre Bestätigung der folgenden rechtlichen Dokumente
          erforderlich. Bitte prüfen Sie die Dokumente und bestätigen Sie diese anschließend.
        </Text>
        {pendingSections.map((section, index) => (
          <div key={section.key}>
            {index > 0 && <Divider mb="md" />}
            <Text fw={600} c="#13135C" style={{ marginBottom: '5px' }}>
              {section.heading}
            </Text>
            <Text fw={400}>
              Version vom {moment(section.versionDate).format('DD.MM.YYYY')}
            </Text>
            <Anchor size="sm" c="#5555FF" href={section.link} target="_blank" rel="noopener noreferrer">
              Dokument anzeigen
              <img src={RightArrowPartner} alt="" style={{ width: '12px', marginLeft: '5px'}}/>
            </Anchor>
            <Checkbox
              mt="xs"
              classNames={{input: 'tenants-list-checkbox-rental-income-input', inner: 'tenants-list-checkbox-rental-income-inner'}}
              styles={{
                label: {
                  fontSize: '14px',
                  paddingLeft: '6px'
                }
              }}
              label={section.checkboxLabel}
              checked={!!checked[section.key]}
              onChange={(event) => {
                const isChecked = event.currentTarget.checked;
                setChecked((prev) => ({ ...prev, [section.key]: isChecked }));
              }}
            />
          </div>
        ))}
        <ColorButton
          text="Fortfahren"
          className={`purple-btn ${!allChecked ? 'disabled' : ''}`}
          showLoader={submitting}
          onClick={handleConfirm}
          buttonStyle={{ marginTop: '10px' }}
          // Mantine's FocusTrap (used by Modal) auto-focuses the first element
          // with data-autofocus, falling back to the first focusable element
          // otherwise — which would be the first "Dokument anzeigen" link.
          buttonConfig={{ 'data-autofocus': true }}
        />
      </Stack>
    </Modal>
  );
}

const mapStateToProps = (state) => ({
  accountData: state.generalReducer.accountData,
});

export default connect(mapStateToProps, { setAccountData })(LegalConsentModal);
